import { useState, useEffect, useRef } from 'react';

const SplashScreen = ({ onComplete }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [progress, setProgress] = useState(0);
  const [canSkip, setCanSkip] = useState(false);
  const animationStartedRef = useRef(false);
  
  // Log only in development
  if (import.meta.env.MODE === 'development') {
    console.log('🎨 SplashScreen render:', { progress, isVisible });
  }

  useEffect(() => {
    // Use ref to prevent double execution in StrictMode
    if (animationStartedRef.current) {
      console.log('🚫 SplashScreen: Animation already started (ref check)');
      return;
    }
    animationStartedRef.current = true;
    console.log('🎬 SplashScreen: Starting animation');
    
    // Simulate app loading - faster for better UX
    const loadingSteps = [
      { step: 'Initializing...', duration: 200 },
      { step: 'Loading family data...', duration: 300 },
      { step: 'Syncing offline data...', duration: 250 },
      { step: 'Preparing dashboard...', duration: 200 },
      { step: 'Ready!', duration: 150 }
    ];

    let currentStep = 0;
    const totalSteps = loadingSteps.length;

    const processStep = () => {
      if (currentStep < totalSteps) {
        setProgress(((currentStep + 1) / totalSteps) * 100);
        
        setTimeout(() => {
          currentStep++;
          processStep();
        }, loadingSteps[currentStep].duration);
      } else {
        // Fade out splash screen
        setTimeout(() => {
          setIsVisible(false);
          setTimeout(() => {
            onComplete && onComplete();
          }, 300);
        }, 500);
      }
    };

    processStep();
    
    // Allow skipping after 1 second
    setTimeout(() => setCanSkip(true), 1000);
  }, [onComplete]);

  const handleSkip = () => {
    setIsVisible(false);
    setTimeout(() => {
      onComplete && onComplete();
    }, 300);
  };

  if (!isVisible) return null;

  return (
    <div className={`fixed inset-0 z-50 bg-stone-200 flex items-center justify-center transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      <div className="text-center space-y-8 px-8">
        {/* App Icon */}
        <div className="w-32 h-32 mx-auto animate-bounce-in">
          <video 
            src="/PaperSedge.mp4" 
            autoPlay
            muted
            playsInline
            loop
            className="w-full h-full rounded-2xl shadow-2xl object-cover"
            aria-label="Paper Sedge animated logo"
            onError={(e) => {
              // Fallback to static icon if video fails
              e.target.style.display = 'none';
              e.target.nextElementSibling.style.display = 'flex';
            }}
          />
          <div className="w-full h-full bg-azure-500 rounded-2xl shadow-2xl hidden items-center justify-center">
            <span className="text-white text-6xl">📄</span>
          </div>
        </div>

        {/* App Name */}
        <div className="space-y-2">
          <h1 className="text-stone-800 text-3xl font-bold tracking-tight">
Paper Sedge          </h1>
          <p className="text-stone-600 text-lg">
Complete family productivity and coordination app          </p>
        </div>

        {/* Loading Progress */}
        <div className="w-64 mx-auto space-y-4">
          <div className="bg-stone-300 rounded-full h-2 overflow-hidden">
            <div 
              className="bg-stone-700 h-full rounded-full transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-stone-600 text-sm">
            {progress < 100 ? `Loading your family experience...` : 'Welcome back!'}
          </p>
        </div>

        {/* Skip button and Version info */}
        <div className="absolute bottom-8 left-0 right-0 text-center">
          {canSkip && (
            <button
              onClick={handleSkip}
              className="mb-4 px-4 py-2 text-stone-600 hover:text-stone-800 text-sm underline transition-colors"
            >
              Skip
            </button>
          )}
          <p className="text-stone-500 text-xs">
            Version 1.0.0 • PWA Ready
          </p>
        </div>
      </div>
    </div>
  );
};

export default SplashScreen;